import { Control, Controller, useFormState } from 'react-hook-form';
import { Box, TextField, useTheme, Typography } from '../../../../../../base';
import {
    StyledOptionsContainer,
    StyledOptionsListWrapper,
} from '../../JobQuestions.styles';
import { useTranslation } from 'react-i18next';
import {
    IJobQuestionsForm,
    JobQuestionObjectFields,
} from '../../../../../../common';
import { ChangeEvent } from 'react';

interface QuestionNumericInputProps {
    fieldNamePrefix?: `questions.${number}`;
    control?: Control<IJobQuestionsForm>;
    index?: number;
    isDealbreaker?: boolean;
    hasBeenSubmitted?: boolean;
}

export const QuestionNumericInput = ({
    fieldNamePrefix,
    control,
    index,
    isDealbreaker,
    hasBeenSubmitted,
}: QuestionNumericInputProps) => {
    const theme = useTheme();
    const { t } = useTranslation();

    const { errors } = useFormState({ control });

    const minValueError = errors?.questions?.[index!]?.bandFrom;
    const maxValueError = errors?.questions?.[index!]?.bandTo;

    // Helper function to format number with commas and up to 2 decimal places
    const formatNumber = (value: string): string => {
        if (!value) return '';

        // Remove all non-numeric characters except decimal point
        const cleanValue = value.replace(/[^\d.]/g, '');

        // Prevent multiple decimal points
        const parts = cleanValue.split('.');
        if (parts.length > 2) {
            return formatNumber(parts[0] + '.' + parts.slice(1).join(''));
        }

        // Format integer part with commas
        parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');

        // Limit decimal places to 2
        if (parts[1] !== undefined) {
            parts[1] = parts[1].substring(0, 2);
        }

        return parts.join('.');
    };

    // Helper function to parse formatted number back to raw value for storage
    const parseNumber = (value: string): string => {
        if (!value) return '';
        return value.replace(/,/g, '');
    };

    // Handle input change with formatting
    const handleNumberChange = (
        e: ChangeEvent<HTMLInputElement>,
        onChange: (value: string) => void
    ) => {
        const inputValue = e.target.value;
        const formatted = formatNumber(inputValue);
        e.target.value = formatted;
        onChange(parseNumber(formatted));
    };

    const inputStyles = {
        '& .MuiInputBase-input': {
            color: theme.palette.text.primary,
        },
    };

    const disabledInputStyles = {
        '& .MuiInputBase-root': {
            backgroundColor: theme.palette.grey[100],
            cursor: 'not-allowed',
        },
        '& .MuiInputBase-input': {
            color: theme.palette.text.disabled,
            WebkitTextFillColor: theme.palette.text.disabled,
        },
    };

    return (
        <StyledOptionsContainer>
            {!isDealbreaker ? (
                // Single numeric input when dealbreaker is disabled
                <Box>
                    <TextField
                        disabled
                        fullWidth
                        size="small"
                        placeholder={t('saveJob.numericPlaceholder')}
                        sx={disabledInputStyles}
                    />
                    <Typography
                        variant="caption"
                        sx={{
                            color: theme.palette.text.secondary,
                            marginTop: theme.spacing(0.5),
                            display: 'block',
                        }}
                    >
                        {t('saveJob.numericHelperText')}
                    </Typography>
                </Box>
            ) : (
                // Range inputs when dealbreaker is enabled
                <Box>
                    <Typography
                        variant="body2"
                        sx={{
                            color: theme.palette.text.primary,
                            marginBottom: theme.spacing(1),
                            fontWeight: 500,
                        }}
                    >
                        {t('saveJob.numericRangeLabel')}
                    </Typography>
                    <StyledOptionsListWrapper>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: theme.spacing(2),
                                flexWrap: 'wrap',
                            }}
                        >
                            {/* Minimum Value Input */}
                            <Box sx={{ flex: '1 1 200px', minWidth: '150px' }}>
                                <Typography
                                    variant="caption"
                                    sx={{
                                        color: theme.palette.text.secondary,
                                        marginBottom: theme.spacing(0.5),
                                        display: 'block',
                                    }}
                                >
                                    {t('saveJob.numericMinValue')}
                                </Typography>
                                <Controller
                                    name={
                                        `${fieldNamePrefix}.${JobQuestionObjectFields.BAND_FROM}` as `questions.${number}.bandFrom`
                                    }
                                    control={control}
                                    render={({
                                        field: { onChange, value, ref },
                                    }) => (
                                        <TextField
                                            inputRef={ref}
                                            fullWidth
                                            size="small"
                                            placeholder="0.00"
                                            value={formatNumber(value || '')}
                                            onChange={(
                                                e: ChangeEvent<HTMLInputElement>
                                            ) =>
                                                handleNumberChange(e, onChange)
                                            }
                                            error={
                                                hasBeenSubmitted &&
                                                !!minValueError
                                            }
                                            helperText={
                                                hasBeenSubmitted &&
                                                minValueError
                                                    ? minValueError.message
                                                    : ''
                                            }
                                            sx={inputStyles}
                                        />
                                    )}
                                />
                            </Box>

                            {/* Separator */}
                            <Typography
                                variant="body1"
                                sx={{
                                    color: theme.palette.text.secondary,
                                    alignSelf: 'flex-end',
                                    marginBottom: theme.spacing(0.5),
                                }}
                            >
                                -
                            </Typography>

                            {/* Maximum Value Input */}
                            <Box sx={{ flex: '1 1 200px', minWidth: '150px' }}>
                                <Typography
                                    variant="caption"
                                    sx={{
                                        color: theme.palette.text.secondary,
                                        marginBottom: theme.spacing(0.5),
                                        display: 'block',
                                    }}
                                >
                                    {t('saveJob.numericMaxValue')}
                                </Typography>
                                <Controller
                                    name={
                                        `${fieldNamePrefix}.${JobQuestionObjectFields.BAND_TO}` as `questions.${number}.bandTo`
                                    }
                                    control={control}
                                    render={({
                                        field: { onChange, value, ref },
                                    }) => (
                                        <TextField
                                            inputRef={ref}
                                            fullWidth
                                            size="small"
                                            placeholder="0.00"
                                            value={formatNumber(value || '')}
                                            onChange={(
                                                e: ChangeEvent<HTMLInputElement>
                                            ) =>
                                                handleNumberChange(e, onChange)
                                            }
                                            error={
                                                hasBeenSubmitted &&
                                                !!maxValueError
                                            }
                                            helperText={
                                                hasBeenSubmitted &&
                                                maxValueError
                                                    ? maxValueError.message
                                                    : ''
                                            }
                                            sx={inputStyles}
                                        />
                                    )}
                                />
                            </Box>
                        </Box>
                    </StyledOptionsListWrapper>
                    <Typography
                        variant="caption"
                        sx={{
                            color: theme.palette.text.secondary,
                            marginTop: theme.spacing(0.5),
                            display: 'block',
                        }}
                    >
                        {t('saveJob.numericRangeHelperText')}
                    </Typography>
                </Box>
            )}
        </StyledOptionsContainer>
    );
};
